import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Circle, Send, Loader2, Volume2, Square, Lightbulb } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { subjectStyle, fmtDue, daysUntil, plusDays } from "@/lib/subjects";
import { Link } from "react-router-dom";
import { W } from "@/lib/edition";

const PRI = { high: "bg-brand text-white", medium: "bg-amber-100 text-amber-800", low: "bg-slate-100 text-slate-600" };

const ActionList = ({ id, items }) => {
  const key = `daybreak-done-${id}`;
  const [done, setDone] = useState(() => { try { return JSON.parse(localStorage.getItem(key)) ?? []; } catch { return []; } });
  useEffect(() => { localStorage.setItem(key, JSON.stringify(done)); }, [key, done]);
  if (!items.length) return <p className="text-sm text-slate-500">Nothing needs doing. Enjoy the calm.</p>;
  return (
    <ul className="space-y-2">
      {items.map((a, i) => {
        const on = done.includes(i);
        return (
          <li key={i}>
            <button data-testid={`action-item-${i}`} onClick={() => setDone((d) => (on ? d.filter((x) => x !== i) : [...d, i]))} className="w-full flex items-start gap-3 rounded-xl bg-surface border border-slate-200 p-3.5 text-left hover:border-teal-400 transition-colors">
              {on ? <CheckCircle2 className="w-5 h-5 mt-0.5 text-teal-600 shrink-0" /> : <Circle className="w-5 h-5 mt-0.5 text-slate-400 shrink-0" />}
              <span className={`flex-1 text-sm sm:text-base ${on ? "line-through text-slate-400" : "text-slate-800"}`}>{a?.text}</span>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-mono uppercase ${PRI[a?.priority] ?? PRI.low}`}>{a?.priority ?? "low"}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const EmailButton = ({ id }) => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/digests/${id}/email`); toast.success(`Sent to ${data.to}`); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid="email-brief-button" onClick={send} disabled={busy} className="relative mt-6 inline-flex items-center gap-2 rounded-xl bg-white/10 border border-white/20 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20 disabled:opacity-60 transition-colors">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Email me this brief
    </button>
  );
};

const DayLessons = ({ label, p, testid }) => (
  <div data-testid={testid} className="min-w-0">
    <div className="flex items-center justify-between gap-2">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] font-bold text-teal-700">{label} · {p?.day}{label !== "Today" && label !== "Tomorrow" && p?.date ? ` ${fmtDue(p.date).split(" ").slice(1).join(" ")}` : ""}</p>
      {p?.week && <span className="rounded-full border border-amber-300 bg-amber-100 px-2.5 py-0.5 font-mono text-[11px] font-bold text-amber-900">Week {p.week}</span>}
    </div>
    {p?.closed ? <p data-testid={`${testid}-closed`} className="mt-3 rounded-xl bg-orange-50 border border-orange-200 px-3 py-2 text-sm font-semibold text-orange-800"><W s="No school" c="Day off" /> · {p.closed}</p> : (p?.lessons ?? []).length === 0 ? <p className="mt-3 text-sm text-slate-500"><W s="No lessons" c="No meetings" /></p> : (
      <ul className="mt-3 space-y-1.5">
        {p.lessons.map((l, i) => (
          <li key={i} className="flex items-center gap-3 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2 text-sm">
            <span className="w-11 shrink-0 font-mono text-xs font-semibold text-slate-500">{l.start}</span>
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: subjectStyle(l.subject).dot }} />
            <span className="flex-1 min-w-0 truncate font-medium text-slate-800">{l.subject}</span>
            {l.room ? <span className="shrink-0 font-mono text-[11px] text-slate-500">{l.room}</span> : null}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const ReviseToday = ({ items, onTick }) => (
  <div data-testid="brief-revise-today" className="mt-5 rounded-2xl border border-gold-400/40 bg-gold-400/10 p-3">
    <p className="font-mono text-[11px] uppercase tracking-[0.18em] font-bold text-gold-300">Revise today</p>
    <ul className="mt-2 space-y-2">
      {items.map((s) => (
        <li key={`${s.hw_id}-${s.idx}`} className="flex items-start gap-2.5 text-sm">
          <button data-testid={`brief-revise-tick-${s.hw_id}-${s.idx}`} onClick={() => onTick?.(s)} aria-label={s.done ? "Mark not done" : "Mark done"}
            className={`mt-0.5 grid place-items-center w-5 h-5 shrink-0 rounded-md border-2 ${s.done ? "bg-emerald-600 border-emerald-600" : "border-gold-300"}`}>{s.done && <CheckCircle2 className="w-3.5 h-3.5" />}</button>
          <span className={s.done ? "line-through text-neutral-400" : ""}>{s.task} <span className="text-neutral-300">· {s.subject || s.test} · {s.minutes} min</span></span>
        </li>
      ))}
    </ul>
  </div>
);

const TestsCard = ({ tests, revision, onTick }) => {
  const rev = Array.isArray(revision) ? revision : [];
  if ((!Array.isArray(tests) || !tests.length) && !rev.length) return null;
  return (
    <section data-testid="brief-tests" className="rounded-3xl bg-navy-900 border border-navy-200 text-white p-5 sm:p-6 shadow-sm">
      <div className="flex items-center justify-between"><h3 className="text-lg font-semibold"><W s="Tests coming up" c="Deadlines coming up" /></h3><Link to="/homework?tab=tests" data-testid="brief-tests-link" className="text-sm font-semibold text-gold-300 hover:underline">See all</Link></div>
      <ul className="mt-4 space-y-2.5">
        {(tests ?? []).map((t) => {
          const n = daysUntil(t.due);
          return (
            <li key={t.hw_id} data-testid={`brief-test-${t.hw_id}`} className="flex items-center gap-3 rounded-2xl bg-white/10 px-3 py-2.5">
              <span className={`grid place-items-center w-12 h-12 shrink-0 rounded-xl text-center leading-none ${n <= 1 ? "bg-brand" : "bg-blue-600"}`}>
                <span><span className="block font-display text-xl">{n === 0 ? "!" : n}</span><span className="block text-[9px] font-bold uppercase tracking-wider">{n === 0 ? "today" : n === 1 ? "day" : "days"}</span></span>
              </span>
              <div className="min-w-0">
                <p className="font-semibold truncate">{t.subject ? `${t.subject}: ` : ""}{t.title}</p>
                <p className="text-xs text-neutral-300 truncate">{fmtDue(t.due)}{t.notes ? ` · ${t.notes}` : ""}</p>
              </div>
            </li>
          );
        })}
      </ul>
      {rev.length > 0 && <ReviseToday items={rev} onTick={onTick} />}
    </section>
  );
};

const HomeworkCard = ({ hw }) => {
  const rows = [["Overdue", hw?.overdue], ["Due today", hw?.today], ["Due tomorrow", hw?.tomorrow]].filter(([, l]) => Array.isArray(l) && l.length);
  if (!rows.length) return null;
  return (
    <section data-testid="brief-homework" className="rounded-3xl bg-surface border border-slate-200 p-5 sm:p-6 shadow-sm">
      <div className="flex items-center justify-between"><h3 className="text-lg font-semibold text-slate-900"><W s="Homework due" c="Tasks due" /></h3><Link to="/homework" data-testid="brief-homework-link" className="text-sm font-semibold text-brand-dark hover:underline">See all</Link></div>
      {rows.map(([label, list]) => (
        <div key={label} className="mt-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] font-bold text-brand-dark">{label}</p>
          <ul className="mt-2 space-y-1.5">{list.map((h) => <li key={h.hw_id} className="flex items-center gap-2.5 text-sm text-slate-800"><span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: subjectStyle(h.subject).dot }} /><span className="flex-1 min-w-0">{h.title}</span>{h.subject ? <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${subjectStyle(h.subject).chip}`}>{h.subject}</span> : null}</li>)}</ul>
        </div>
      ))}
    </section>
  );
};

const ListenButton = ({ text }) => {
  const [on, setOn] = useState(false);
  useEffect(() => () => { window.speechSynthesis?.cancel(); }, []);
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const toggle = () => {
    const s = window.speechSynthesis;
    if (on) { s.cancel(); setOn(false); return; }
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "en-GB";
    u.rate = 1;
    const v = s.getVoices().find((x) => x.lang === "en-GB");
    if (v) u.voice = v;
    u.onend = () => setOn(false);
    s.cancel();
    s.speak(u);
    setOn(true);
  };
  return (
    <button data-testid="brief-listen-button" onClick={toggle} className="mt-4 ml-2 inline-flex items-center gap-2 rounded-xl border border-white/30 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 transition-colors">
      {on ? <Square className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}{on ? "Stop" : "Listen"}
    </button>
  );
};

const SchoolCard = ({ tt }) => {
  const brk = (tt.calendar?.upcoming ?? []).find((c) => c.label !== "INSET day" && c.label !== "Bank holiday");
  return (
  <section data-testid="school-card" className="rounded-3xl bg-surface border border-slate-200 p-5 sm:p-6 shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="text-lg font-semibold text-slate-900"><W s="School" c="Schedule" /> <Link to="/week" data-testid="school-week-link" className="ml-2 text-sm font-semibold text-gold-300 hover:underline">Week view</Link></h3>
      {brk && <span data-testid="school-next-break" className="rounded-full bg-navy-900 border border-gold-400/60 px-3 py-1 text-xs font-semibold text-gold-300">{brk.label}: {fmtDue(brk.start)} – {fmtDue(brk.end)}</span>}
    </div>
    <div className="mt-4 grid gap-5 sm:grid-cols-2">
      {tt.evening && tt.after ? (
        <>
          <DayLessons label={tt.next?.date === plusDays(1) ? "Tomorrow" : "Next"} p={tt.next} testid="school-next" />
          <DayLessons label="Then" p={tt.after} testid="school-after" />
        </>
      ) : (
        <>
          <DayLessons label="Today" p={tt.today} testid="school-today" />
          <DayLessons label="Next" p={tt.next} testid="school-next" />
        </>
      )}
    </div>
    {(tt.next_kit ?? []).length > 0 && (
      <div data-testid="brief-kit" className="mt-4 flex flex-wrap items-center gap-2 rounded-xl bg-amber-50 border border-amber-100 p-3">
        <span className="text-sm font-semibold text-slate-900"><W s="Pack for" c="Bring on" /> {tt.next?.day}:</span>
        {tt.next_kit.map((k) => <span key={k.item} className="rounded-full bg-surface border border-amber-200 px-2.5 py-0.5 text-xs font-semibold text-amber-800">{k.item}</span>)}
      </div>
    )}
  </section>
  );
};

const PlanCard = ({ plan }) => {
  const items = Array.isArray(plan) ? plan.filter((p) => p?.task) : [];
  if (!items.length) return null;
  return (
    <section data-testid="brief-plan" className="rounded-3xl bg-surface border border-slate-200 p-5 sm:p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-900"><W s="After-school plan" c="Plan for the day" /></h3>
      <ol className="mt-4 relative border-l-2 border-gold-400/50 ml-2 space-y-3">
        {items.map((p, i) => (
          <li key={i} data-testid={`brief-plan-${i}`} className="relative pl-5">
            <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-gold-400 ring-4 ring-white" />
            <p className="text-sm text-slate-800"><b className="font-mono text-gold-300 mr-2">{p.time}</b>{p.task}{p.minutes ? <span className="text-slate-500"> · {p.minutes} min</span> : null}</p>
          </li>
        ))}
      </ol>
    </section>
  );
};

const TipCard = ({ tip }) => (tip?.text ? (
  <section data-testid="brief-tip" className="mb-6 rounded-3xl border border-amber-200 bg-amber-50 p-5">
    <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] font-bold text-amber-900"><Lightbulb className="w-4 h-4" />Tip{tip.subject ? ` for ${tip.subject}` : ""}</p>
    <p data-testid="brief-tip-text" className="mt-2 text-sm sm:text-base text-slate-800 leading-relaxed">{tip.text}</p>
  </section>
) : null);

export const BriefView = ({ digest, onRevisionTick }) => {
  const r = digest?.result ?? {};
  const actions = Array.isArray(r.action_items) ? r.action_items : [];
  const top3 = Array.isArray(r.top3) ? r.top3.filter(Boolean).slice(0, 3) : [];
  return (
    <div className="space-y-6">
      <motion.section data-testid="brief-hero" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-3xl bg-navy-900 text-white p-6 sm:p-10 border-b-4 border-gold-400">
        <div className="absolute -right-16 -bottom-40 w-56 h-56 sm:-right-24 sm:-bottom-32 sm:w-80 sm:h-80 rounded-full bg-brand/60 sm:bg-brand/70 sunrise" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-gold-400/20 to-transparent" />
        <div className="relative max-w-3xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold-300">The headline</p>
          <h2 data-testid="brief-headline" className="mt-3 font-display text-4xl sm:text-5xl leading-[1.05]">{r.headline ?? "—"}</h2>
          <p data-testid="brief-overview" className="mt-4 text-slate-200 text-base sm:text-lg leading-relaxed">{r.overview}</p>
          {top3.length > 0 && (
            <ol data-testid="brief-top3" className="mt-5 space-y-2">
              {top3.map((t, i) => (
                <li key={i} data-testid={`brief-top3-${i}`} className="flex items-start gap-3 text-base sm:text-lg"><span className="grid place-items-center w-7 h-7 shrink-0 rounded-full bg-gold-400 font-bold text-sm text-black">{i + 1}</span><span className="pt-0.5">{t}</span></li>
              ))}
            </ol>
          )}
          <EmailButton id={digest.digest_id} />
          <ListenButton text={`${r.headline ?? ""}. ${r.overview ?? ""} ${top3.join(". ")}`} />
        </div>
      </motion.section>
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="space-y-6">
          {digest?.timetable && <SchoolCard tt={digest.timetable} />}
          <PlanCard plan={r.plan} />
          <TestsCard tests={digest?.homework?.tests} revision={digest?.revision} onTick={onRevisionTick} />
          <HomeworkCard hw={digest?.homework} />
        </section>
        <section>
          <TipCard tip={r.tip} />
          <h3 className="mb-3 text-lg font-semibold text-slate-900">To do</h3>
          <ActionList id={digest.digest_id} items={actions} />
        </section>
      </div>
    </div>
  );
};
